import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule, NgComponentOutlet } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { NgbDropdownModule, NgbPaginationModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { NgChartsModule } from 'ng2-charts';
import { AutosizeModule } from 'ngx-autosize';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { NgxFileDropModule } from 'ngx-file-drop';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { ToastrModule } from 'ngx-toastr';
import { TranslateService } from '../core/translate';
import { DateFilterComponent } from '../requests-detail-view/date-filter/date-filter.component';
import { AddAttachmentsComponent } from './components/attachments/add-attachments/add-attachments.component';
import { AttachmentItemComponent } from './components/attachments/attachments-list/attachment-item/attachment-item.component';
import { AttachmentsListComponent } from './components/attachments/attachments-list/attachments-list.component';
import { UploadAttachmentsComponent } from './components/attachments/upload-attachments/upload-attachments.component';
import { AuthorizedModalComponent } from './components/authorized-modal/authorized-modal.component';
import { BackBtnComponent } from './components/back-btn/back-btn.component';
import { BarChartComponent } from './components/bar-chart/bar-chart.component';
import { ClientEmployeeCountCardComponent } from './components/client-employee-count-card/client-employee-count-card.component';
import { CountCardComponent } from './components/count-card/count-card.component';
import { CustomTableComponent } from './components/custom-table/custom-table.component';
import { DatePickerCustomHeaderComponent } from './components/date-picker-custom-header/date-picker-custom-header.component';
import { DoughnutChartComponent } from './components/doughnut-chart/doughnut-chart.component';
import { DynamicModalComponent } from './components/dynamic-modal/dynamic-modal.component';
import { EmptyStateComponent } from './components/empty-state/empty-state.component';
import { LineChartComponent } from './components/line-chart/line-chart.component';
import { LoaderComponent } from './components/loader/loader.component';
import { TableToCardsComponent } from './components/mobile-view/table-to-cards/table-to-cards.component';
import { MonthDatePickerComponent } from './components/month-date-picker/month-date-picker.component';
import { NotAthorizedPageComponent } from './components/notAthorizedPage/notAthorizedPage/not-athorized-page.component';
import { DateRangeInputComponent } from './components/service-input-details/date-range-input/date-range-input.component';
import { ServiceInputDetailsComponent } from './components/service-input-details/service-input-details.component';
import { TimeRangeInputComponent } from './components/service-input-details/time-range-input/time-range-input.component';
import { SessionExpiredComponent } from './components/sessionExpired/sessionExpired/session-expired.component';
import { SnackbarAlertsComponent } from './components/snackbar-alerts/snackbar-alerts.component';
import { TpImgComponent } from './components/tp-img/tp-img.component';
import { TqwFilterDropdownComponent } from './components/tqw-filter-dropdown/tqw-filter-dropdown.component';
import { TqwListLoopingComponent } from './components/tqw-list-looping/tqw-list-looping.component';
import { TqwStatusComponent } from './components/tqw-status/tqw-status.component';
import { TqwViewItemComponent } from './components/tqw-view-item/tqw-view-item.component';
import { TqwViewComponent } from './components/tqw-view/tqw-view.component';
import { TwqLoaderComponent } from './components/twq-loader/twq-loader.component';
import { TwqLoadingSpinnerComponent } from './components/twq-loading-spinner/twq-loading-spinner.component';
import { TwqNotFoundDataComponent } from './components/twq-not-found-data/twq-not-found-data.component';
import { TwqPaginationComponent } from './components/twq-pagination/twq-pagination.component';
import { CustomDateAdapter } from './custom-date-adapter';
import { IfSuperAdminGeneralManagerDirective } from './directives/if-super-admin-generalManager.directive';
import { IfSuperAdminSectionManagerAdminDirective } from './directives/if-super-admin-sectionManger-admin.directive';
import { IfSuperAdminDirective } from './directives/if-super-admin.directive';
import { MaterialModule } from './material/material.module';
import { DateFormatPipe } from './pipes/date-format.pipe';
import { FileTypePipe } from './pipes/file-type.pipe';
import { FirstMiddleAndLastName } from './pipes/first-middle-last-name.pipe';
import { FormatNumberAsTextPipe } from './pipes/formats-number-as-text.pipe';
import { GetAdmissionServiceNamePipe } from './pipes/get-admission-service-name.pipe';
import { GetAdmissionStatusPipe } from './pipes/get-admission-status.pipe';
import { GetKeyValueByLangPipe } from './pipes/get-key-value-by-lang.pipe';
import { GetOperationStatusPipe } from './pipes/get-operation-status.pipe';
import { GetStrategyImageByLangPipe } from './pipes/get-strategy-image-by-lang.pipe';
import { GetValueByLangPipe } from './pipes/get-value-by-lang.pipe';
import { HandleCardItemsListStatusAndRole } from './pipes/handle-card-items-list-status-and-role.pipe';
import { HandleCardItemsListStatus } from './pipes/handle-card-items-list-status.pipe';
import { IsMenuOpenedPipe } from './pipes/is-menu-opened.pipe';
import { LimitToPipe } from './pipes/limit-to.pipe';
import { MapValuePipe } from './pipes/map-value.pipe';
import { PaginablePipe } from './pipes/paginable.pipe';
import { RequestActionNamePipe } from './pipes/request-action-name.pipe';
import { RequestActionPipe } from './pipes/request-action.pipe';
import { RequestListEmptyStateMessagePipe } from './pipes/request-list-empty-state-message.pipe';
import { RequestServiceLevelAgreementPipe } from './pipes/request-service-level-agreement.pipe';
import { ShortNamePipe } from './pipes/short-name.pipe';
import { TableDataFormatPipe } from './pipes/table-data-format.pip';
import { UserImagePipe } from './pipes/user-image.pipe';
import { SelectDropdownComponent } from './select-dropdown/select-dropdown.component';
import { ScreenResizeService } from './services/screen-resize.service';

export const DD_MM_YYYY_Format = {
    parse: {
        dateInput: 'LL'
    },
    display: {
        dateInput: 'LL D MMM YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY'
    }
};

@NgModule({
    declarations: [
        FormatNumberAsTextPipe,
        RequestListEmptyStateMessagePipe,
        RequestServiceLevelAgreementPipe,
        RequestActionPipe,
        RequestActionNamePipe,
        TableDataFormatPipe,
        SnackbarAlertsComponent,
        EmptyStateComponent,
        FileTypePipe,
        ServiceInputDetailsComponent,
        AddAttachmentsComponent,
        AttachmentsListComponent,
        AttachmentItemComponent,
        DatePickerCustomHeaderComponent,
        TimeRangeInputComponent,
        DateRangeInputComponent,
        BackBtnComponent,
        DoughnutChartComponent,
        BarChartComponent,
        LineChartComponent,
        CountCardComponent,
        MonthDatePickerComponent,
        DateFilterComponent,
        LoaderComponent,
        ClientEmployeeCountCardComponent,
        SelectDropdownComponent,
        FirstMiddleAndLastName,
        ShortNamePipe,
        UserImagePipe,
        UploadAttachmentsComponent,
        SessionExpiredComponent,
        NotAthorizedPageComponent,
        PaginablePipe,
        LimitToPipe,
        IsMenuOpenedPipe,
        HandleCardItemsListStatusAndRole,
        HandleCardItemsListStatus,
        TpImgComponent,
        GetAdmissionServiceNamePipe,
        GetAdmissionStatusPipe,
        GetOperationStatusPipe,
        IfSuperAdminDirective,
        IfSuperAdminSectionManagerAdminDirective,
        IfSuperAdminGeneralManagerDirective,
        TableToCardsComponent,
        GetValueByLangPipe,
        GetStrategyImageByLangPipe,
        DateFormatPipe,
        GetKeyValueByLangPipe,
        DynamicModalComponent,
        CustomTableComponent,
        TwqLoadingSpinnerComponent,
        TwqNotFoundDataComponent,
        MapValuePipe,
        TwqPaginationComponent,
        TqwFilterDropdownComponent,
        TqwStatusComponent,
        TqwListLoopingComponent,
        TqwViewItemComponent,
        TqwViewComponent,
        AuthorizedModalComponent,
        TwqLoaderComponent
    ],
    imports: [
        CommonModule,
        TranslateModule.forChild(),
        FormsModule,
        MatMenuModule,
        ReactiveFormsModule,
        MatSelectModule,
        MaterialModule,
        InfiniteScrollModule,
        NgbPaginationModule,
        NgxFileDropModule,
        AutosizeModule,
        NgbTooltipModule,
        NgbDropdownModule,
        NgChartsModule,
        MatTabsModule,
        BsDatepickerModule.forRoot(),
        ToastrModule.forRoot(),
        DragDropModule,
        MatDialogModule,
        MatIconModule,
        NgComponentOutlet,
        NgxMaterialTimepickerModule.setOpts('en-GB'),
        MatInputModule,
        MatFormFieldModule
    ],
    exports: [
        CommonModule,
        TranslateModule,
        FormsModule,
        ReactiveFormsModule,
        MaterialModule,
        FormatNumberAsTextPipe,
        RequestListEmptyStateMessagePipe,
        RequestActionPipe,
        RequestActionNamePipe,
        RequestServiceLevelAgreementPipe,
        TableDataFormatPipe,
        InfiniteScrollModule,
        NgbPaginationModule,
        EmptyStateComponent,
        NgxFileDropModule,
        AutosizeModule,
        FileTypePipe,
        NgbTooltipModule,
        ServiceInputDetailsComponent,
        AddAttachmentsComponent,
        AttachmentsListComponent,
        AttachmentItemComponent,
        DatePickerCustomHeaderComponent,
        NgbDropdownModule,
        BackBtnComponent,
        NgChartsModule,
        DoughnutChartComponent,
        BarChartComponent,
        LineChartComponent,
        CountCardComponent,
        BsDatepickerModule,
        MonthDatePickerComponent,
        DateFilterComponent,
        LoaderComponent,
        ClientEmployeeCountCardComponent,
        SelectDropdownComponent,
        FirstMiddleAndLastName,
        ShortNamePipe,
        UserImagePipe,
        SessionExpiredComponent,
        PaginablePipe,
        LimitToPipe,
        IsMenuOpenedPipe,
        HandleCardItemsListStatusAndRole,
        HandleCardItemsListStatus,
        TpImgComponent,
        GetAdmissionServiceNamePipe,
        GetAdmissionStatusPipe,
        GetOperationStatusPipe,
        MatTabsModule,
        IfSuperAdminDirective,
        IfSuperAdminSectionManagerAdminDirective,
        IfSuperAdminGeneralManagerDirective,
        TableToCardsComponent,
        GetValueByLangPipe,
        GetStrategyImageByLangPipe,
        DateFormatPipe,
        GetKeyValueByLangPipe,
        DragDropModule,
        DynamicModalComponent,
        CustomTableComponent,
        TwqLoadingSpinnerComponent,
        TwqNotFoundDataComponent,
        MapValuePipe,
        TwqPaginationComponent,
        TqwFilterDropdownComponent,
        TqwStatusComponent,
        TqwListLoopingComponent,
        TqwViewItemComponent,
        TqwViewComponent,
        AuthorizedModalComponent,
        TwqLoaderComponent
    ],
    providers: [
        ScreenResizeService,
        { provide: DateAdapter, useClass: CustomDateAdapter },
        { provide: MAT_DATE_FORMATS, useValue: DD_MM_YYYY_Format },
        { provide: MAT_DATE_LOCALE, useValue: (trans: TranslateService) => trans.getLangFromCookie() }
    ]
})
export class SharedModule {}
